import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
  styled,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUserId } from "@utils/userUtils";
import { getUserFacilities } from "@services/facilityManagement";
import type { UserFacility } from "@services/facilityManagement";
import {
  getCustomerAgreementMappings,
  acceptCustomerAgreements,
  FlattenedAgreement,
} from "@services/agreements";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import {
  filterFacilitiesEligibleForAgreements,
  getFacilityCustomerId,
  facilityLabel,
  resolveInitialFacility,
} from "@utils/agreementsFacilities";

/** Same title band as Regulations Library / Profile (Discovery 80px + 20px). */
const PAGE_TITLE_BAND_HEIGHT_PX = 100;

const PageContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  minHeight: "100%",
  height: "auto",
  flex: "1 0 auto",
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  position: "relative",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
});

const HeaderContainer = styled(Box)({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  flexShrink: 0,
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 1,
});

const TitleBand = styled(Box)(({ theme }) => ({
  height: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  padding: "0 64px 24px",
  display: "flex",
  alignItems: "flex-end",
  boxSizing: "border-box",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "0 16px 16px",
    height: "auto",
    minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
    alignItems: "flex-end",
  },
}));

const HeaderTitleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const HeaderIconWrapper = styled(Box)({
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const HeaderIcon = styled(DescriptionOutlined)({
  fontSize: "28px",
  color: "#065b70",
});

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: "1 0 auto",
  padding: "16px 48px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  overflow: "visible",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "12px 16px 24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px 24px",
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "28px",
  lineHeight: 1.5,
  letterSpacing: "-1px",
  color: "#262626",
  textAlign: "left",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#4b5563",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
  },
}));

/** Subtitle sits just under the title; negative margin cancels title band bottom padding. */
const HeaderSubtitleRow = styled(Box)(({ theme }) => ({
  padding: "0 64px 12px",
  marginTop: "-20px",
  [theme.breakpoints.down("md")]: {
    padding: "0 16px 12px",
    marginTop: "-12px",
  },
}));

const Card = styled(Box)(() => ({
  marginTop: "12px",
  borderRadius: "16px",
  border: "1px solid #e5f5f9",
  backgroundColor: "#ffffff",
  boxShadow: "0px 4px 18px rgba(10,130,157,0.08)",
  padding: "20px 24px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
}));

type LocationState = { customerId?: number } | null | undefined;

const AgreementsAcceptancePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAgreements, setPendingAgreements] = useState<FlattenedAgreement[]>([]);
  const [eligibleFacilities, setEligibleFacilities] = useState<UserFacility[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const eligibleFacilitiesRef = useRef<UserFacility[]>([]);
  eligibleFacilitiesRef.current = eligibleFacilities;

  const applyMappingsForFacility = useCallback(
    async (facilityId: number, facilitiesList?: UserFacility[]) => {
      const list = facilitiesList ?? eligibleFacilitiesRef.current;
      const row = list.find((f) => f.id === facilityId);
      const cid = row ? getFacilityCustomerId(row) : null;
      if (cid == null) {
        throw new Error("Customer id is not available for the selected facility.");
      }
      setSelectedFacilityId(facilityId);
      setCustomerId(cid);
      const mappings = await getCustomerAgreementMappings(1, 100, {
        customer_id: cid,
      });
      const mapped = mappings.agreements || [];
      const pending = mapped.filter((a) => !a.acceptance_status);
      if (mapped.length > 0 && pending.length === 0) {
        navigate("/dashboard/agreements", { replace: true, state: { customerId: cid } });
        return;
      }
      setPendingAgreements(pending);
      setAckChecked(false);
    },
    [navigate],
  );

  const loadData = useCallback(async (preferredCustomerId?: number | null) => {
    setLoading(true);
    setError(null);
    try {
      const userIdStr = getCurrentUserId();
      const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
      if (!userId || Number.isNaN(userId)) {
        throw new Error("User is not available. Please sign in again.");
      }

      const facilitiesRes = await getUserFacilities(userId);
      const raw = Array.isArray(facilitiesRes.facilities) ? facilitiesRes.facilities : [];
      const facilities = filterFacilitiesEligibleForAgreements(raw);
      setEligibleFacilities(facilities);

      if (!facilities.length) {
        throw new Error("No facilities found for the current user.");
      }

      const initial = resolveInitialFacility(facilities, preferredCustomerId ?? undefined);
      if (!initial) {
        throw new Error("Customer id is not available for the selected facility.");
      }
      await applyMappingsForFacility(initial.id, facilities);
    } catch (err: any) {
      setError(err?.message || "Unable to load agreements for acceptance.");
      setPendingAgreements([]);
      setAckChecked(false);
      setCustomerId(null);
      setSelectedFacilityId(null);
    } finally {
      setLoading(false);
    }
  }, [applyMappingsForFacility]);

  const reloadFacilitySelection = useCallback(
    async (facilityId: number) => {
      setLoading(true);
      setError(null);
      try {
        await applyMappingsForFacility(facilityId);
      } catch (err: any) {
        setError(err?.message || "Unable to load agreements for acceptance.");
        setPendingAgreements([]);
        setAckChecked(false);
      } finally {
        setLoading(false);
      }
    },
    [applyMappingsForFacility],
  );

  useEffect(() => {
    const preferred = (location.state as LocationState)?.customerId;
    void loadData(typeof preferred === "number" ? preferred : undefined);
  }, [loadData, location.key]);

  const hasAny = pendingAgreements.length > 0;

  return (
    <PageContainer>
      <HeaderContainer>
        <TitleBand
          style={{
            height: PAGE_TITLE_BAND_HEIGHT_PX,
            minHeight: PAGE_TITLE_BAND_HEIGHT_PX,
            padding: "0 64px 24px",
            display: "flex",
            alignItems: "flex-end",
            boxSizing: "border-box",
          }}
        >
          <HeaderTitleRow>
            <HeaderIconWrapper>
              <HeaderIcon />
            </HeaderIconWrapper>
            <Title>Accept Agreement</Title>
          </HeaderTitleRow>
        </TitleBand>
        <HeaderSubtitleRow>
          <Subtitle>
            Your partner requests that you accept the UniComply Customer Agreement.
          </Subtitle>
        </HeaderSubtitleRow>
      </HeaderContainer>
      <ContentContainer>
          {eligibleFacilities.length > 0 && selectedFacilityId != null ? (
            <FormControl fullWidth size="small" sx={{ maxWidth: 600 }}>
              <InputLabel id="agreements-accept-facility-label">Facility</InputLabel>
              <Select
                labelId="agreements-accept-facility-label"
                label="Facility"
                value={selectedFacilityId}
                disabled={loading}
                renderValue={(selected) => {
                  const facility = eligibleFacilities.find((f) => f.id === Number(selected));
                  if (!facility) return "";
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <BusinessOutlined
                        sx={{ fontSize: 18, color: "#465a63", flexShrink: 0 }}
                      />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {facilityLabel(facility)}
                      </span>
                    </Box>
                  );
                }}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!Number.isNaN(v) && v !== selectedFacilityId) {
                    void reloadFacilitySelection(v);
                  }
                }}
              >
                {eligibleFacilities.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <BusinessOutlined
                        sx={{ fontSize: 18, color: "#465a63", flexShrink: 0 }}
                      />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {facilityLabel(f)}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {error ? (
            <Alert
              severity="error"
              sx={{ mt: 1, mb: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => void loadData()}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : null}

          <Card>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : !hasAny ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Rethink Sans, sans-serif",
                  fontSize: "16px",
                  color: "#262626",
                  textAlign: "center",
                  lineHeight: 1.6,
                }}
              >
                No agreements available at the moment.
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: "10px",
                  border: "1px solid #e5f5f9",
                  backgroundColor: "#f9fafb",
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#065B70",
                    marginBottom: 0.5,
                  }}
                >
                  Agreements to accept:
                </Typography>
                {pendingAgreements.map((a) => (
                <Box
                  key={a.agreement_id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    gap: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontSize: "16px",
                      color: "#111827",
                      flex: "0 0 auto",
                    }}
                  >
                    •
                  </Typography>
                  <Link
                    href={a.agreement_url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontSize: "16px",
                      color: a.agreement_url ? "#065B70" : "#262626",
                      pointerEvents: a.agreement_url ? "auto" : "none",
                      cursor: a.agreement_url ? "pointer" : "default",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={a.agreement_display_name}
                  >
                    {a.agreement_display_name}
                  </Link>
                </Box>
                ))}
              </Box>

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={ackChecked}
                    onChange={(e) => setAckChecked(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontSize: "16px",
                      color: "#262626",
                      lineHeight: 1.6,
                    }}
                  >
                    Yes, I acknowledge that I have read and understand the UniComply Customer
                    Agreement and that I am authorized on behalf of my organization to agree to
                    these terms and accept the agreement.
                  </Typography>
                }
              />

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/dashboard/agreements")}
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #b3b3b3",
                    borderRadius: "8px",
                    padding: "12px 24px",
                    textTransform: "none",
                    color: "#262626",
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    boxShadow: "0px 4px 4px 0px rgba(194,194,194,0.25)",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#b3b3b3",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!ackChecked || !customerId || submitting}
                  onClick={async () => {
                    if (!customerId || !hasAny || submitting) return;
                    try {
                      setSubmitting(true);
                      await acceptCustomerAgreements(customerId);
                      setAckChecked(false);
                      navigate("/dashboard/agreements", {
                        replace: true,
                        state: { customerId },
                      });
                    } catch (err: any) {
                      setError(err?.message || "Unable to accept agreements.");
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  sx={{
                    backgroundColor: "#0a829d",
                    color: "#e6fafc",
                    textTransform: "none",
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#065b70",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#b0c4c8",
                      color: "#e6fafc",
                    },
                  }}
                >
                  {submitting ? "Accepting..." : "Accept Agreement"}
                </Button>
              </Box>
            </>
          )}
        </Card>
      </ContentContainer>
    </PageContainer>
  );
};

export default AgreementsAcceptancePage;
